import {
  Configuration,
  PublicClientApplication,
  AuthenticationResult,
  AccountInfo,
} from "@azure/msal-browser";
import { EventEmitter, IEventEmitter } from "@eaigle/utils/eventEmitter";
import { isInFrameOrPopup } from "./authFrame";

export type AuthEvents = {
  revokeToken: string;
  logoutError: Error;
};

/** Resolves the MSAL extraQueryParameters to send with each request. */
export type ExtraQueryParametersResolver = () => Record<string, string> | undefined;

export interface IAuthenticationService extends IEventEmitter<AuthEvents> {
  getAccount(): AccountInfo | null;
  getAccessToken(): Promise<AuthenticationResult | null>;
  logout(): Promise<void>;
}

class AuthenticationService implements IAuthenticationService {
  private _app: PublicClientApplication | null = null;
  private _msalConfig: Configuration = { auth: { clientId: "" } };
  private _scope: string = "";
  private _getExtraQueryParameters: ExtraQueryParametersResolver | undefined;
  private initialized = false;
  private interactionPromise: Promise<void> | null = null;
  /** Guard flag to prevent re-entrant or concurrent logout calls */
  private isLoggingOut = false;

  /**
   * Provide the PublicClientApplication instance that MsalProvider is using.
   * Must be called once in main.tsx before React renders, so that authService
   * and MsalProvider share the same MSAL instance and avoid the dual-instance
   * conflict that leaves acquireTokenSilent in an inconsistent state on first load.
   */
  configure(
    app: PublicClientApplication,
    msalConfig: Configuration,
    scope: string,
    getExtraQueryParameters?: ExtraQueryParametersResolver,
  ): void {
    this._app = app;
    this._msalConfig = msalConfig;
    this._scope = scope;
    this._getExtraQueryParameters = getExtraQueryParameters;
    this.initialized = true;
  }

  private get app(): PublicClientApplication {
    if (!this._app) {
      // Fallback: create a standalone instance (used in tests where
      // configure() is never called). In production configure() is always called.
      this._app = new PublicClientApplication(this._msalConfig);
    }
    return this._app;
  }

  private eventEmitter = new EventEmitter<AuthEvents>();
  on = this.eventEmitter.on.bind(this.eventEmitter);
  off = this.eventEmitter.off.bind(this.eventEmitter);

  /**
   * Returns the configured API OAuth scope from environment variables.
   * Falls back to an empty string if the env var is not set or not a string.
   */
  private get scope(): string {
    return this._scope;
  }

  /**
   * Resolved on every request rather than cached, so a sign-in domain change
   * applies to token renewal without reconfiguring the service.
   */
  private get extraQueryParameters(): Record<string, string> | undefined {
    return this._getExtraQueryParameters?.();
  }

  /**
   * Initializes the MSAL PublicClientApplication instance once.
   * Handles any pending redirect promises from a prior auth redirect,
   * and waits for any in-progress interaction to settle before proceeding.
   */
  private async initialize(): Promise<void> {
    if (!this.initialized) {
      await this.app.initialize();
      // Handle any pending redirects exactly once on initialization.
      // This resolves the MSAL interaction_in_progress state after a redirect flow.
      this.interactionPromise = this.app
        .handleRedirectPromise()
        .then(() => {
          this.interactionPromise = null;
        })
        .catch(() => {
          // Redirect promise errors are non-fatal during initialization.
          // The app will render in an unauthenticated state and let the user retry.
          this.interactionPromise = null;
        });
      this.initialized = true;
    }

    // Wait for any in-progress interaction (redirect or silent) to complete
    // before allowing further token operations.
    if (this.interactionPromise) {
      await this.interactionPromise;
    }
  }

  /**
   * Returns the currently active account, or the only available account
   * if exactly one exists. Returns null if no account is found or if
   * multiple accounts exist without an explicitly active one.
   */
  public getAccount(): AccountInfo | null {
    const activeAccount = this.app.getActiveAccount();
    if (activeAccount) return activeAccount;

    const allAccounts = this.app.getAllAccounts();

    // If only one account exists, implicitly use it
    if (allAccounts.length === 1) return allAccounts[0];

    // Multiple accounts detected but none are active — ambiguous, return null.
    // The UI should prompt the user to select an account in this case.
    return null;
  }

  /**
   * Attempts to acquire an access token silently using the cached account.
   * On any failure, triggers an interactive redirect so the user can re-authenticate
   * rather than leaving the app stuck on a loading screen indefinitely.
   */
  getAccessToken = async (): Promise<AuthenticationResult | null> => {
    try {
      await this.initialize();

      const account = this.getAccount();
      if (!account) {
        // No account available — let the UI render the unauthenticated state.
        // Do NOT call logout() here as it would cause a redirect loop when
        // no account is present (e.g. on first load or after session expiry).
        return null;
      }

      try {
        // Attempt silent token acquisition using the cached account and token
        return await this.app.acquireTokenSilent({
          account,
          scopes: [this.scope],
          extraQueryParameters: this.extraQueryParameters,
        });
      } catch {
        // Don't do anything else inside the MSAL iframe
        if (isInFrameOrPopup()) {
          return null;
        }

        // Silent acquisition failed for any reason — expired token, iframe timeout
        // (monitor_window_timeout), stale cache, interaction_required, etc.
        // Redirect to login rather than returning null, which would leave the app
        // stuck on "Validating your access…" with no recovery path.
        if (!this.interactionPromise) {
          this.interactionPromise = this.app
            .acquireTokenRedirect({
              scopes: [this.scope],
              account,
              extraQueryParameters: this.extraQueryParameters,
            })
            .then(() => {
              this.interactionPromise = null;
            })
            .catch(() => {
              this.interactionPromise = null;
              void this.logout();
            });
          await this.interactionPromise;
        }

        return null;
      }
    } catch {
      // Unexpected error in the token acquisition pipeline.
      // Return null instead of logging out to avoid cascading failures.
      return null;
    }
  };

  /**
   * Logs the user out by:
   * 1. Attempting to silently acquire a token to emit a revokeToken event
   *    (e.g. for backend session invalidation). Failure here is non-fatal.
   * 2. Clearing the active account locally.
   * 3. Redirecting to the IdP logout endpoint.
   *
   * Protected by an isLoggingOut guard to prevent concurrent or recursive calls.
   */
  public async logout(): Promise<void> {
    // Don't do anything inside the MSAL iframe.
    if (isInFrameOrPopup()) return;

    // Prevent concurrent or re-entrant logout calls (e.g. from multiple tabs
    // or from error handlers firing while logout is already in progress)
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    try {
      await this.initialize();

      // Hoist account once — used for both token revoke and logoutRedirect hint
      const account = this.getAccount();

      try {
        // Attempt to acquire the current token silently so we can emit a
        // revokeToken event for any backend session cleanup listeners.
        // This uses acquireTokenSilent directly (not getAccessToken) to
        // avoid re-entering this class's error handling logic.
        if (account) {
          const accessToken = await this.app.acquireTokenSilent({
            account,
            scopes: [this.scope],
          });
          if (accessToken) {
            await this.eventEmitter.emit("revokeToken", accessToken.accessToken);
          }
        }
      } catch {
        // Token revoke failure is non-fatal — proceed with IdP logout regardless
      }

      // Clear the locally cached active account before redirecting
      this.app.setActiveAccount(null);

      // Redirect to the IdP logout endpoint.
      // Passing account as a hint may allow faster session termination on the IdP side.
      await this.app.logoutRedirect({
        account,
        postLogoutRedirectUri: "/",
      });
    } catch (error) {
      // TODO: Implement logout error handling (e.g. show user-facing error toast)
      void this.eventEmitter.emit("logoutError", error as Error);
    } finally {
      // Always reset the guard, even if logout throws, so future attempts can proceed
      this.isLoggingOut = false;
    }
  }
}

export const authService = new AuthenticationService();
